<template>
    <div v-if="validFilter">
      <MultiSelect
        v-if="isMultiSelect"
        :name="dimension.key"
        :label="filterLabel"
        :options="options"
        :value="filterValue"
        :displayLimit="3"
        :error="error || tooManySubcategoriesSelected"
        @input="handleMultiSelectInput($event)"
        @toggle="handleOpen(dimension, $event)"
      >
        <template
          v-if="showBadge"
          v-slot:badge="{ badge }"
        >
          <SecondaryBadge
            v-if="badge"
            class="selected-badge"
            :style="multiSelectBadgeStyling"
          >
            <div class="selected-badge-content">
              +{{ badge }}
            </div>
          </SecondaryBadge>
        </template>
        <template v-slot:noResults>
          <LoadingDarkIcon v-if="isFetching"></LoadingDarkIcon>
          {{ loadingMessage }}
        </template>
      </MultiSelect>
      <Select
        v-else
        :name="dimension.key"
        :label="filterLabel"
        :options="options"
        :value="filterValue"
        @input="handleSelectInput($event, true)"
      >
      </Select>
    </div>
</template>
<script>
import { mapState, mapGetters } from 'vuex';
import {
  LoadingDarkIcon,
  SecondaryBadge,
  MultiSelect,
  Select,
} from '@mikmak/components';
import { getBenchmarksFilterValues } from '../../../services/dashboard/benchmarksService';
import { CommonLogger } from '../../../services/logger';
import utils from '../../../services/utils';

export default {
  props: {
    value: {
      type: Array,
    },
    category: {
      type: String,
    },
    dimension: {
      type: Object,
    },
    dateRange: {
      type: Object,
    },
    comparisonRange: {
      type: Object,
    },
    singleSelectBrandId: {
      type: String,
    },
    selectedCategoryId: {
      type: [Number, String],
      default: '',
    },
    selectedSubcategoryIds: {
      type: Array,
    },
    portfolioBrands: {
      type: Array,
    },
    useSalesInsightsIfAvailable: {
      type: Boolean,
    },
    isMultiSelect: {
      type: Boolean,
      default: true,
    },
    showBadge: {
      type: Boolean,
      default: false,
    },
    error: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    LoadingDarkIcon,
    SecondaryBadge,
    MultiSelect,
    Select,
  },
  data() {
    return {
      localValue: this.value,
      options: [],
      localPortfolioBrands: this.portfolioBrands,
      isFetching: false,
      brandBenchmarksOnly: ['campaignName', 'audience', 'adUnitType'],
      multiSelectBadgeStyling: {
        pointerEvents: 'none',
        position: 'absolute',
        width: '40px',
        right: '35px',
        top: '3px',
      },
    };
  },
  mounted() {
    if (this.isBrandDimension) {
      const brandOptions = this.localPortfolioBrands.map(({ key, value }) => {
        return { key, value };
      });

      utils.sortArrayByKey(brandOptions, 'key');

      this.options = brandOptions;

      if (!this.isMultiSelect && this.singleSelectBrandId) {
        this.handleSelectInput(this.filterValue);
      }
    }

    if (this.isCategoryOrSubcategoryDimension) {
      this.options = this.specialDropdownOptions();
      if (this.isCategoryDimension) this.handleSelectInput(this.filterValue);
      else this.handleMultiSelectInput(this.filterValue);
    }

    if (this.hasError) {
      this.$emit('error', true);
    }
  },
  computed: {
    ...mapState({
      activeBenchmarksView: state => state.benchmarks.activeBenchmarksView,
      categoriesAndSubcategories: state => state.dashboard.categoriesAndSubcategories,
    }),
    ...mapGetters([
      'benchmarksCategories',
    ]),
    formattedBenchmarksCategories() {
      return this.benchmarksCategories.map(c => {
        const formattedCategoryName = this.formatCategoryName(c.key);

        return ({ ...c, key: formattedCategoryName });
      });
    },
    validFilter() {
      return !this.isBrandDimension || this.portfolioBrands.length >= 1;
    },
    isBrandDimension() {
      return this.dimension.value === 'brandName';
    },
    isCategoryDimension() {
      return this.dimension.value === 'category';
    },
    isSubcategoryDimension() {
      return this.dimension.value === 'subcategory';
    },
    isCategoryOrSubcategoryDimension() {
      return this.isCategoryDimension || this.isSubcategoryDimension;
    },
    isMultiSelectBrandDimension() {
      return this.isBrandDimension && this.isMultiSelect;
    },
    loadingMessage() {
      return this.isFetching ? 'Loading results' : 'No results';
    },
    filterValue() {
      if (this.localValue === undefined || this.localValue === null) return 'null';

      if (this.isBrandDimension && !this.isMultiSelect) {
        return this.singleSelectBrandId;
      }

      if (this.isCategoryDimension) {
        return this.selectedCategoryId;
      }

      if (this.isSubcategoryDimension) {
        return this.selectedSubcategoryIds
          .map(sc => this.categoriesAndSubcategories[sc]);
      }

      return this.localValue;
    },
    filterLabel() {
      return this.dimension.placeholder || this.dimension.key;
    },
    tooManySubcategoriesSelected() {
      return this.isSubcategoryDimension
        && this.selectedSubcategoryIds
        && this.selectedSubcategoryIds.length > 3;
    },
    missingBrandWhenNeeded() {
      return this.isBrandDimension &&
        !this.isMultiSelectBrandDimension &&
        !this.singleSelectBrandId;
    },
    missingCategoryWhenNeeded() {
      return this.isCategoryDimension
        && !this.selectedCategoryId;
    },
    hasError() {
      return this.missingBrandWhenNeeded ||
        this.missingCategoryWhenNeeded ||
        this.tooManySubcategoriesSelected;
    },
  },
  methods: {
    handleOpen(dimension, isOpen) {
      if (isOpen
        && !this.isMultiSelectBrandDimension
        && !this.isCategoryDimension
      ) {
        this.getDimensionValues(dimension);
      }
    },
    async getDimensionValues(dimension) {
      if (this.isSubcategoryDimension) {
        this.specialDropdownOptions();

        return;
      }

      this.isFetching = true;

      const payload = {
        brandId: this.singleSelectBrandId,
        initialStart: this.dateRange.start,
        initialEnd: this.dateRange.end,
        comparisonStart: this.comparisonRange.start,
        comparisonEnd: this.comparisonRange.end,
        dimension: dimension.value,
        context: this.activeBenchmarksView,
        categoryId: this.selectedCategoryId,
      };

      try {
        const token = await this.$auth.getTokenSilently();
        const data = await getBenchmarksFilterValues(payload, token);

        this.setFilters(data);
      } catch (error) {
        CommonLogger.error('Error retrieving dashboard filter values', {
          error,
        });
      } finally {
        this.isFetching = false;
      }
    },
    specialDropdownOptions() {
      if (this.isCategoryDimension) {
        return this.formattedBenchmarksCategories;
      } else if (this.isSubcategoryDimension && this.selectedCategoryId) {
        return this.getSubcategoryOptions();
      }

      return [];
    },
    getSubcategoryOptions() {
      const selectedCategoryObj = this.categoriesAndSubcategories[this.selectedCategoryId];

      return selectedCategoryObj.subcategories
        .map(sc => this.categoriesAndSubcategories[sc]);
    },
    setFilters(values) {
      this.options = values.filters
        ? values.filters.map(val => {

          // Have to handle passing non-null key-value pairs to the MultiSelect component
          const isUndefinedOrNull = val === undefined || val === null;
          const valKey = isUndefinedOrNull ? 'null' : val.toString();
          const value = isUndefinedOrNull ? 'null' : val;

          return {
            key: valKey,
            value: value,
          };
        })
        : [];
    },
    handleSelectInput(val, updateOtherDropdowns = false) {
      if (this.isBrandDimension) {
        this.localValue = [this.portfolioBrands.find(pb => pb.value === val)];

        this.$emit(
          'updateSingleSelectBrand',
          {
            brand: this.localValue[0],
            updateOtherDropdowns,
          },
        );
      }

      if (this.isCategoryDimension && val) {
        this.localValue = [this.categoriesAndSubcategories[val]];

        this.$emit(
          'updateSelectedCategory',
          {
            categoryId: val,
            updateOtherDropdowns,
          },
        );
      }

      this.handleInput();
    },
    handleMultiSelectInput(val) {
      // Have to check if we passed a null string value as
      // non-null key-value pairs are not allowed in the MultiSelect component
      const containsNull = val && val.some(v => v.value === 'null');

      this.localValue = containsNull
        ? val.reduce((nullValues, v) => {
          const newVal = v.value === 'null' ? { key: v.key, value: null } : v;

          return nullValues.concat(newVal);
        }, [])
        : val;

      if (this.isSubcategoryDimension) {
        this.$emit('updateSelectedSubcategories', val);
      }

      this.handleInput();
    },
    handleInput() {
      const formattedValue = {};

      formattedValue[this.dimension.value] = this.localValue;

      this.$emit('input', formattedValue);
    },
    formatCategoryName(string = '') {
      const stringWithoutUnderscores = string.replaceAll('_', ' ');

      return utils.stringToTitleCase(stringWithoutUnderscores);
    },
  },
  watch: {
    value() {
      this.localValue = this.value;
    },
    selectedCategoryId() {
      if (this.isSubcategoryDimension) {
        this.options = this.selectedCategoryId
          ? this.getSubcategoryOptions()
          : [];
      }
    },
    hasError(newVal) {
      this.$emit('error', newVal);
    },
    singleSelectBrandId() {
      if (!this.isBrandDimension && !this.isCategoryDimension) {
        this.localValue = [];
        this.handleInput();
      }
    },
  },
};
</script>
<style scoped>
.selected-badge-content {
  margin: auto;
}
.selected-badge button.secondary {
  background: #00A6A4;
  width: 100%;
}
</style>
